import React, {ComponentType, createContext, useContext, useEffect, useLayoutEffect, useState} from 'react';
import {useControllerProps} from '../../../../Widget/ControllerContext';
import {FormInstanceData, useFormInstance} from '../../../../Form/useFormInstance';
import {AddressWithContactModel} from '../../../../../../domain/models/checkout/Cart/AddressWithContact.model';
import {useMemberDetailsData} from '../../../../MemberDetails/WithMemberDetailsData';
import {ADD_NEW_ADDRESS_ID} from '../../../../constants';
import {ContactModel} from '../../../../../../domain/models/checkout/Cart/Contact.model';
import {isContactDefined} from '../../../../../../domain/utils/isContactDefined';
import {isShippingDestinationSignificant} from '../../../../../../domain/utils/isShippingDestinationSignificant';
import {AddressModel} from '../../../../../../domain/models/checkout/Cart/Address.model';
import {FormValues} from '@wix/form-fields';
import {
  getCustomFieldFromAdditionalInfoFormValues,
  getCustomerDetailsFormInitialState,
  getExtendedFieldValuesFromCustomerDetailsForm,
  getEmailFromEmailFormValues,
  getSubscriptionFromFormValues,
} from '../../../../Form/CustomerDetailsForm/CustomerDetailsForm.utils';
import {
  getAddressFromAddressFormValues,
  getContactFormCurrentStateWithUpdatedCountry,
  getContactDetailsFromContactFormValues,
} from '../../../../Form/form.utils';
import {SPECS} from '../../../../../../common/constants';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {useLocaleKeys} from '../../../../../../locale-keys/LocaleKeys';

export type CustomerDetailsDataContextType = {
  customerDetailsForm: FormInstanceData;
  isFormValid: () => Promise<boolean>;
  updateContactCountry: (country: string) => void;
  getCustomerDetailsForSubmit: () => {
    contactDetails?: ContactModel;
    email?: string;
    extendedFieldsValue?: any;
    customFieldValue?: string;
    shippingAddress?: AddressModel;
    shouldSubscribe?: boolean;
  };
  contactCountry?: string;
};

export const CustomerDetailsDataContext = createContext({} as CustomerDetailsDataContextType);

export function withCustomerDetailsData<T extends object>(Component: ComponentType<T>) {
  /* eslint-disable-next-line sonarjs/cognitive-complexity */
  return function Wrapper(props: T) {
    const {
      checkoutStore: {cart, isShippingFlow, isFastFlow},
      checkoutSettingsStore: {checkoutSettings},
      memberStore: {isMember, defaultAddressId, addressesInfo},
      formsStore: {dataExtendedFieldsTargets, shouldSubscribe, doesFormHaveSubscription, doesFormHaveMultilineAddress},
    } = useControllerProps();
    const {experiments} = useExperiments();
    const localeKeys = useLocaleKeys();
    const enableConcatStreetAddress = experiments.enabled(SPECS.EnableConcatStreetAddress);

    function getDefaultAddressFromAddresses(): AddressWithContactModel | undefined {
      return addressesInfo?.addresses.find((address) => address.addressId === defaultAddressId);
    }

    function getInitialContact(): ContactModel | undefined {
      if (isShippingDestinationSignificant(cart.deliveryInfo, isShippingFlow)) {
        return cart.deliveryInfo.contact;
      }

      if (!isShippingFlow && isContactDefined(cart.paymentInfo.contact)) {
        return cart.paymentInfo.contact;
      }

      if (!isMember) {
        return cart.deliveryInfo.contact;
      }

      return getDefaultAddressFromAddresses()?.contact;
    }

    function getInitialAddress(): AddressModel | undefined {
      if (!isMember || isShippingDestinationSignificant(cart.deliveryInfo, isShippingFlow)) {
        const address = cart.deliveryInfo.address;
        const shouldOverrideAddressLine = enableConcatStreetAddress
          ? !address?.addressLine && address?.streetAddress?.name && address?.streetAddress?.number
          : false;

        if (!shouldOverrideAddressLine) {
          return address;
        }

        return {
          ...address,
          addressLine: localeKeys.checkout.address.addressLine({
            streetName: address?.streetAddress?.name,
            streetNumber: address?.streetAddress?.number,
          }),
        };
      }

      return getDefaultAddressFromAddresses()?.address;
    }

    const {editMode, selectedAddressesService, selectedAddressesServiceId} = useMemberDetailsData();

    const customerDetailsForm = useFormInstance(
      () =>
        getCustomerDetailsFormInitialState({
          checkoutSettings,
          address: getInitialAddress(),
          email: cart.customerInfo.email ?? '',
          contact: getInitialContact(),
          customField: cart.customField,
          extendedFields: cart.extendedFields,
          shouldSubscribe,
          isFastFlow,
          doesFormHaveSubscription,
          doesFormHaveMultilineAddress,
        }) as FormValues
    );

    const isFormValid = async () => customerDetailsForm.isValid();

    const areFormsRendered = () => customerDetailsForm.isRendered();

    const getCustomerDetailsForSubmit = () => {
      const addressWithContact = areFormsRendered()
        ? {
            customerContact: getContactDetailsFromContactFormValues(customerDetailsForm.data.formValues),
            deliveryAddress: isShippingFlow
              ? getAddressFromAddressFormValues(customerDetailsForm.data.formValues, doesFormHaveMultilineAddress)
              : undefined,
          }
        : {};
      return {
        ...addressWithContact,
        email: !isMember ? getEmailFromEmailFormValues(customerDetailsForm.data.formValues) : cart.customerInfo.email,
        ...{
          extendedFieldsValue: getExtendedFieldValuesFromCustomerDetailsForm(
            customerDetailsForm.data.formValues,
            dataExtendedFieldsTargets
          ),
        },
        customFieldValue: getCustomFieldFromAdditionalInfoFormValues(customerDetailsForm.data.formValues),
        shouldSubscribe: getSubscriptionFromFormValues(customerDetailsForm.data.formValues),
      };
    };

    const initForm = (shippingDestination: AddressWithContactModel) => {
      customerDetailsForm.data.setFormValues(
        getCustomerDetailsFormInitialState({
          checkoutSettings,
          address: shippingDestination?.address,
          contact: shippingDestination?.contact,
          customField: cart.customField,
          extendedFields: cart.extendedFields,
          shouldSubscribe,
          isFastFlow,
          doesFormHaveSubscription,
          doesFormHaveMultilineAddress,
        }) as FormValues
      );
    };

    const [contactCountry, setContactCountry] = useState(cart.deliveryInfo.address?.country);

    const updateContactCountry = (country: string) => {
      setContactCountry(country);

      customerDetailsForm.data.setFormValues(
        getContactFormCurrentStateWithUpdatedCountry({
          contactFormValues: customerDetailsForm.data.formValues,
          country,
        })
      );
    };

    const useEffectOrLayoutEffect = experiments.enabled(SPECS.UseLayoutEffectInCheckout)
      ? useLayoutEffect
      : /* istanbul ignore next */ useEffect;

    useEffectOrLayoutEffect(
      () => {
        if (selectedAddressesServiceId === ADD_NEW_ADDRESS_ID) {
          let contactDetailsForFirstTimeMember: ContactModel | undefined;
          if (
            isMember &&
            addressesInfo.addresses.length === 0 &&
            !isShippingFlow &&
            isContactDefined(cart.paymentInfo.contact)
          ) {
            contactDetailsForFirstTimeMember = cart.paymentInfo.contact;
          }
          initForm(
            new AddressWithContactModel({
              addressId: ADD_NEW_ADDRESS_ID,
              contact: contactDetailsForFirstTimeMember
                ? new ContactModel(contactDetailsForFirstTimeMember)
                : undefined,
            })
          );
        } else if (editMode) {
          initForm(selectedAddressesService!);
        }
      },
      /* eslint-disable react-hooks/exhaustive-deps*/ [editMode, selectedAddressesServiceId]
    );

    return (
      <CustomerDetailsDataContext.Provider
        value={{
          customerDetailsForm,
          isFormValid,
          getCustomerDetailsForSubmit,
          updateContactCountry,
          contactCountry,
        }}>
        <Component {...props} />
      </CustomerDetailsDataContext.Provider>
    );
  };
}

export function useCustomerDetailsData() {
  const {customerDetailsForm, isFormValid, getCustomerDetailsForSubmit, updateContactCountry, contactCountry} =
    useContext(CustomerDetailsDataContext);

  return {
    customerDetailsFormData: customerDetailsForm.data,
    isFormValid,
    getCustomerDetailsForSubmit,
    updateContactCountry,
    contactCountry,
  };
}
